import { formatUtcDatetimeToColombian } from "@/helpers/time";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Heading,
  Portal,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { canCreateContract, formatDocument } from "../columns";
import {
  occupationLabels,
  personType,
  propertyTypeLabels,
  propertyUseLabels,
  sourceLabels,
} from "../mappers/screening.mapper";
import { Screening } from "../types";
import DetailList, { Detail } from "./DetailList";
import DetailSection from "./DetailSection";
import Participants from "./Participants";
import StatusBadge from "./StatusBadge";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreateContract: (screening: Screening) => void;
  screening?: Screening;
};

const statusDetailHeadings: Partial<Record<Screening["status"], string>> = {
  rejected: "Motivo",
};

type FormatCurrency = (value: number) => string;

const documentLabels: Record<
  NonNullable<Screening["documents"]>[number]["type"],
  string
> = {
  camaraComercio: "Cámara de Comercio",
  rut: "RUT",
  bankStatements: "Extractos bancarios",
  incomeTaxReturn: "Declaración de renta",
};

// Portal screenings come with the tenant's contact, property and company
// data; every field is optional on the entity, so each row checks presence.
const buildPortalDetails = (
  screening: Screening,
  formatCurrency: FormatCurrency
): Detail[] => [
  ...(screening.requestedMerchantName
    ? [
        {
          label: "Inmobiliaria indicada",
          value: screening.requestedMerchantName,
        },
      ]
    : []),
  ...(screening.rentsThroughMerchant === false
    ? [{ label: "Inmobiliaria", value: "Sin inmobiliaria" }]
    : []),
  ...(screening.tenantOccupation
    ? [
        {
          label: "Ocupación",
          value: occupationLabels[screening.tenantOccupation],
        },
      ]
    : []),
  ...(screening.tenantCity
    ? [{ label: "Ciudad de la empresa", value: screening.tenantCity }]
    : []),
  ...(screening.propertyCity
    ? [{ label: "Ciudad", value: screening.propertyCity }]
    : []),
  ...(screening.adminFee !== undefined || screening.vatFee !== undefined
    ? [
        {
          label: "Total mensual",
          value: formatCurrency(
            screening.monthlyRent +
              (screening.adminFee ?? 0) +
              (screening.vatFee ?? 0)
          ),
        },
      ]
    : []),
  ...(screening.vatFee
    ? [{ label: "IVA", value: formatCurrency(screening.vatFee) }]
    : []),
  ...(screening.commercialUseDeclaration
    ? [
        {
          label: "Declaración de uso comercial",
          value: `Aceptada el ${formatUtcDatetimeToColombian(
            screening.commercialUseDeclaration.acceptedAt
          )}`,
        },
      ]
    : []),
  ...(screening.documents ?? []).map((document) => ({
    label: documentLabels[document.type],
    value: document.fileName,
  })),
];

const buildDetails = (
  screening: Screening,
  formatCurrency: FormatCurrency
): Detail[] => [
  { label: "Inmueble", value: screening.propertyAddress || "-" },
  {
    label: "Uso",
    value: screening.propertyUse
      ? propertyUseLabels[screening.propertyUse]
      : "-",
  },
  ...(screening.propertyType
    ? [
        {
          label: "Tipo de inmueble",
          value: propertyTypeLabels[screening.propertyType],
        },
      ]
    : []),
  { label: "Canon mensual", value: formatCurrency(screening.monthlyRent) },
  // Portal-created screenings carry what the tenant typed; agent-sourced rows
  // do not have these, so they only render when present.
  ...(screening.adminFee !== undefined
    ? [{ label: "Administración", value: formatCurrency(screening.adminFee) }]
    : []),
  ...(screening.declaredIncome !== undefined
    ? [
        {
          label: "Ingresos declarados",
          value: formatCurrency(screening.declaredIncome),
        },
      ]
    : []),
  ...buildPortalDetails(screening, formatCurrency),
  ...(screening.employeeId
    ? [{ label: "Asesor (ID)", value: screening.employeeId }]
    : []),
  { label: "Canal", value: sourceLabels[screening.source] },
  { label: "Estado", value: <StatusBadge status={screening.status} /> },
  {
    label: "Creado",
    value: formatUtcDatetimeToColombian(screening.createdAt),
  },
  ...(screening.approvedAt
    ? [
        {
          label: "Aprobado el",
          value: formatUtcDatetimeToColombian(screening.approvedAt),
        },
      ]
    : []),
];

const ScreeningDetailModal = ({
  open,
  onClose,
  onCreateContract,
  screening,
}: Props): JSX.Element => {
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");
  const statusDetailHeading =
    (screening && statusDetailHeadings[screening.status]) ?? "Detalle";

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => !e.open && onClose()}
      size="md"
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            {screening && (
              <>
                <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
                  <Dialog.Title>
                    <Heading>{screening.tenantName}</Heading>
                    <Text textStyle="sm" color="gray.500" fontWeight="normal">
                      {formatDocument(screening)}
                      {screening.tenantDocumentType &&
                        ` · ${personType(screening.tenantDocumentType)}`}
                    </Text>
                  </Dialog.Title>
                </Dialog.Header>
                <Dialog.CloseTrigger asChild>
                  <CloseButton marginTop="1" />
                </Dialog.CloseTrigger>
                <Dialog.Body py={6}>
                  <VStack gap={6} align="stretch">
                    <DetailList
                      details={buildDetails(screening, formatCurrency)}
                    />
                    <DetailSection title="Participantes">
                      <Participants screening={screening} />
                    </DetailSection>
                    {screening.statusDetail && (
                      <Box>
                        <Separator borderColor="gray.200" mb={4} />
                        <Text fontWeight={600} color="gray.600" mb={2}>
                          {statusDetailHeading}
                        </Text>
                        <Text color="gray.700">{screening.statusDetail}</Text>
                      </Box>
                    )}
                    {canCreateContract(screening) && (
                      <Box bg="#E3FFE4" borderRadius="md" p={3}>
                        <Text fontSize="sm" color="#1D7A5C">
                          <b>Estudio aprobado.</b> Ya puedes ingresar el
                          contrato y emitir el certificado.
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer borderTop="1px solid #ebecef80">
                  <Button variant="outline" onClick={onClose}>
                    Cerrar
                  </Button>
                  {canCreateContract(screening) && (
                    <Button onClick={() => onCreateContract(screening)}>
                      Ingresar contrato
                    </Button>
                  )}
                </Dialog.Footer>
              </>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ScreeningDetailModal;
